'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard,
  TrendingUp,
  Bell,
  Settings,
  ChevronLeft,
  ChevronRight,
  Search,
  Database,
  LogOut,
  HelpCircle,
  Zap,
} from 'lucide-react';

const navGroups = [
  {
    label: 'Analytics',
    items: [
      { href: '/branded-traffic-dashboard', icon: LayoutDashboard, label: 'Dashboard', badge: null },
      { href: '/branded-traffic-dashboard', icon: TrendingUp, label: 'Spike Events', badge: '3' },
      { href: '/branded-traffic-dashboard', icon: Search, label: 'Query Explorer', badge: null },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { href: '/branded-traffic-dashboard', icon: Database, label: 'GSC Properties', badge: null },
      { href: '/branded-traffic-dashboard', icon: Bell, label: 'Alert Rules', badge: '1' },
      { href: '/branded-traffic-dashboard', icon: Settings, label: 'Settings', badge: null },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`
        relative flex flex-col bg-white border-r border-[#e7e7e7]
        transition-all duration-300 ease-in-out flex-shrink-0
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo / Brand */}
      <div
        className={`flex items-center h-16 px-4 border-b border-[#e7e7e7] ${
          collapsed ? 'justify-center' : 'gap-3'
        }`}
      >
        {/* Smarty-style logo mark */}
        <div className="w-8 h-8 rounded-[5px] bg-[#961932] flex items-center justify-center flex-shrink-0">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M9 2L15.5 6V12L9 16L2.5 12V6L9 2Z" stroke="white" strokeWidth="1.5" fill="none" />
            <circle cx="9" cy="9" r="2.5" fill="white" />
          </svg>
        </div>
        {!collapsed && (
          <div className="flex flex-col">
            <span className="font-semibold text-[14px] text-[#39364E] tracking-tight leading-tight">
              BrandedTracker
            </span>
            <span className="text-[10px] text-[#696969] leading-tight">by Smarty</span>
          </div>
        )}
      </div>

      {/* GSC Connection Status */}
      {!collapsed && (
        <div className="mx-3 mt-3 px-3 py-2 bg-[rgba(150,25,50,0.06)] border border-[rgba(150,25,50,0.2)] rounded-[5px] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
          <span className="text-xs text-[#39364E] font-medium truncate">GSC Connected</span>
          <Zap size={12} className="text-[#961932] ml-auto flex-shrink-0" />
        </div>
      )}
      {collapsed && (
        <div
          className="mx-auto mt-3 w-8 h-8 rounded-[5px] bg-[rgba(150,25,50,0.06)] border border-[rgba(150,25,50,0.2)] flex items-center justify-center"
          title="GSC Connected"
        >
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
        </div>
      )}

      {/* Nav Groups */}
      <nav className="flex-1 px-2 py-4 space-y-4 overflow-y-auto scrollbar-thin">
        {navGroups?.map((group) => (
          <div key={`group-${group?.label}`}>
            {!collapsed && (
              <p className="px-3 mb-1.5 text-[10px] font-600 uppercase tracking-widest text-[#696969]">
                {group?.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group?.items?.map((item) => {
                const isActive = pathname === item?.href && item?.label === 'Dashboard';
                const ItemIcon = item?.icon;
                return (
                  <li key={`nav-${item?.label}`}>
                    <Link
                      href={item?.href}
                      className={`
                        group relative flex items-center gap-3 px-3 py-2 rounded-[5px] text-sm font-medium
                        transition-all duration-150
                        ${
                          isActive
                            ? 'bg-[rgba(150,25,50,0.08)] text-[#961932]'
                            : 'text-[#696969] hover:bg-[rgba(150,25,50,0.05)] hover:text-[#39364E]'
                        }
                        ${collapsed ? 'justify-center px-0' : ''}
                      `}
                      title={collapsed ? item?.label : undefined}
                    >
                      <ItemIcon
                        size={18}
                        className={`flex-shrink-0 ${isActive ? 'text-[#961932]' : ''}`}
                      />
                      {!collapsed && (
                        <span className="flex-1 truncate">{item?.label}</span>
                      )}
                      {!collapsed && item?.badge && (
                        <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#961932] text-white text-[10px] font-600 flex items-center justify-center tabular-nums">
                          {item?.badge}
                        </span>
                      )}
                      {collapsed && item?.badge && (
                        <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-[#961932]" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom Actions */}
      <div className="px-2 py-3 border-t border-[#e7e7e7] space-y-0.5">
        <button
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-[5px] text-sm font-medium text-[#696969] hover:bg-[rgba(150,25,50,0.05)] hover:text-[#39364E] transition-all duration-150 ${
            collapsed ? 'justify-center px-0' : ''
          }`}
          title={collapsed ? 'Help' : undefined}
        >
          <HelpCircle size={18} />
          {!collapsed && <span>Help & Docs</span>}
        </button>
        <button
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-[5px] text-sm font-medium text-[#696969] hover:bg-red-50 hover:text-red-600 transition-all duration-150 ${
            collapsed ? 'justify-center px-0' : ''
          }`}
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={18} />
          {!collapsed && <span>Sign Out</span>}
        </button>

        {/* User */}
        {!collapsed && (
          <div className="flex items-center gap-2 px-3 py-2 mt-1">
            <div className="w-7 h-7 rounded-full bg-[rgba(150,25,50,0.12)] flex items-center justify-center flex-shrink-0">
              <span className="text-[11px] font-600 text-[#961932]">MK</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-600 text-[#39364E] truncate">Maya Krishnan</p>
              <p className="text-[10px] text-[#696969] truncate">maya@acme.io</p>
            </div>
          </div>
        )}
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white border border-[#e7e7e7] flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-150 z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}